import {Link} from 'react-router-dom';
import {Spinner} from '@/components/ui/Spinner';
import {downloadInvoice} from '@/api/billing';
import {formatNumber} from '@tryghost/shade/utils';
import {useInvoices} from '@/hooks/useBilling';
import {useMediaQuery} from '@/hooks/useMediaQuery';
import {useState} from 'react';
import type {Invoice} from '@/types/api';

const Icons = {
    Download: () => (
        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" x2="12" y1="15" y2="3" />
        </svg>
    ),
    Receipt: () => (
        <svg fill="none" height="32" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="32">
            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
            <path d="M8 6h8M8 10h8M8 14h4" />
        </svg>
    ),
    ArrowLeft: () => (
        <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
            <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
    ),
    ChevronLeft: () => (
        <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    ),
    ChevronRight: () => (
        <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    )
};

function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

function formatCurrency(cents: number, currency: string = 'USD') {
    return new Intl.NumberFormat('en-US', {style: 'currency', currency}).format(cents / 100);
}

function InvoiceStatusBadge({status}: { status: string }) {
    const styles: Record<string, { bg: string; color: string; border: string }> = {
        paid: {bg: 'var(--studio-soft)', color: 'var(--tt-text-secondary)', border: 'var(--studio-soft)'},
        pending: {bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.2)'},
        failed: {bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.2)'},
        refunded: {bg: 'var(--tt-table-header-bg)', color: 'var(--tt-text-secondary)', border: 'transparent'}
    };
    const s = styles[status] || styles.pending;

    return (
        <span
            style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'capitalize',
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`
            }}
        >
            {status}
        </span>
    );
}

function SiteBadge({text, color}: { text: string; color: string }) {
    return (
        <span
            style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: '1.1rem',
                fontWeight: 500,
                background: `color-mix(in srgb, ${color} 8%, transparent)`,
                color: color
            }}
        >
            {text}
        </span>
    );
}

const ITEMS_PER_PAGE = 15;

export function AllInvoicesPage() {
    const {isMobile, isTablet} = useMediaQuery();
    const isCompact = isMobile || isTablet;
    const [page, setPage] = useState(0);

    const {data: invoicesData, isLoading} = useInvoices(ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    const invoices = invoicesData?.invoices || [];
    const total = invoicesData?.total || 0;
    const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

    const handleDownloadInvoice = async (invoiceId: string, invoiceNumber: string) => {
        try {
            const blob = await downloadInvoice(invoiceId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `invoice-${invoiceNumber}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch {
        }
    };

    return (
        <div className="studio-content-page">
            {/* Header */}
            <div style={{marginBottom: 24}}>
                <Link
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        color: 'var(--tt-text-secondary)',
                        fontSize: '1.3rem',
                        textDecoration: 'none',
                        marginBottom: 12
                    }}
                    to="/billing"
                >
                    <Icons.ArrowLeft />
                    Back to Billing
                </Link>
                <h1 className="studio-page-title">
                    All Invoices
                </h1>
                <p style={{fontSize: '1.4rem', color: 'var(--tt-text-secondary)', margin: '8px 0 0 0'}}>
                    View and download all your invoices across all publications
                </p>
            </div>

            {/* Invoices Table */}
            <div className="studio-panel"
                style={{background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, overflow: 'hidden'}}
            >
                {isLoading ? (
                    <div style={{padding: 48, textAlign: 'center'}}>
                        <Spinner size={80} />
                    </div>
                ) : invoices.length === 0 ? (
                    <div style={{padding: '64px 24px', textAlign: 'center'}}>
                        <div
                            style={{
                                width: 64,
                                height: 64,
                                borderRadius: 16,
                                background: 'var(--tt-table-header-bg)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                color: 'var(--tt-text-tertiary)'
                            }}
                        >
                            <Icons.Receipt />
                        </div>
                        <p style={{fontSize: '1.6rem', fontWeight: 600, color: 'var(--tt-text-primary)', margin: '0 0 8px 0'}}>
                            No invoices yet
                        </p>
                        <p style={{fontSize: '1.4rem', color: 'var(--tt-text-tertiary)', margin: 0}}>
                            Invoices will appear here after your first payment
                        </p>
                    </div>
                ) : (
                    <>
                        {invoices.map((invoice: Invoice, index: number) => (
                            <div
                                key={invoice.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: isCompact ? 'flex-start' : 'center',
                                    flexDirection: isCompact ? 'column' : 'row',
                                    gap: isCompact ? 12 : 16,
                                    padding: '16px 20px',
                                    borderBottom: index < invoices.length - 1 ? '1px solid var(--tt-divider)' : 'none',
                                    transition: 'background 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--tt-table-header-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                <div style={{flex: 1}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap'}}>
                                        <span
                                            style={{
                                                fontSize: '1.4rem',
                                                fontWeight: 600,
                                                color: 'var(--tt-text-primary)',
                                                fontFamily: 'monospace'
                                            }}
                                        >
                                            {invoice.invoice_number}
                                        </span>
                                        <InvoiceStatusBadge status={invoice.status} />
                                        {invoice.site_name && (
                                            <SiteBadge color="#6366f1" text={invoice.site_name} />
                                        )}
                                    </div>
                                    <p style={{fontSize: '1.2rem', color: 'var(--tt-text-tertiary)', margin: 0}}>
                                        {formatDate(invoice.paid_at || invoice.created_at)}
                                        {invoice.description && ` — ${invoice.description}`}
                                    </p>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        width: isCompact ? '100%' : 'auto',
                                        justifyContent: isCompact ? 'space-between' : 'flex-end'
                                    }}
                                >
                                    <span style={{fontSize: '1.6rem', fontWeight: 700, color: 'var(--tt-text-primary)'}}>
                                        {formatCurrency(invoice.amount_cents, invoice.currency)}
                                    </span>
                                    {invoice.status === 'paid' && (
                                        <button className="studio-control" style={{display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, background: 'var(--tt-table-header-bg)', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-secondary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease'}}
                                            type="button"
                                            onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = 'var(--tt-accent)';
                                                e.currentTarget.style.color = 'var(--tt-accent)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = 'var(--tt-card-border)';
                                                e.currentTarget.style.color = 'var(--tt-text-secondary)';
                                            }}
                                        >
                                            <Icons.Download />
                                            PDF
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '16px 20px',
                                    borderTop: '1px solid var(--tt-divider)',
                                    background: 'var(--tt-table-header-bg)'
                                }}
                            >
                                <span style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)'}}>
                                    Showing {formatNumber(page * ITEMS_PER_PAGE + 1)}-{formatNumber(Math.min((page + 1) * ITEMS_PER_PAGE, total))} of {formatNumber(total)}
                                </span>
                                <div style={{display: 'flex', gap: 8}}>
                                    <button className="studio-control" disabled={page === 0}
                                        style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: page === 0 ? 'var(--tt-text-tertiary)' : 'var(--tt-text-secondary)', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1, fontWeight: 500, fontSize: '13px', fontFamily: 'inherit'}}
                                        type="button"
                                        onClick={() => setPage(p => Math.max(0, p - 1))}
                                    >
                                        <Icons.ChevronLeft />
                                    </button>
                                    <button className="studio-control" disabled={page >= totalPages - 1}
                                        style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: page >= totalPages - 1 ? 'var(--tt-text-tertiary)' : 'var(--tt-text-secondary)', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.5 : 1, fontWeight: 500, fontSize: '13px', fontFamily: 'inherit'}}
                                        type="button"
                                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                    >
                                        <Icons.ChevronRight />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
